import { Document, Page } from "react-pdf";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { ensurePdfWorker } from "../pdfWorkerSetup";
import { fitWidth, FALLBACK_ASPECT } from "./pdfFit";

// Не await'им: родитель (PdfView/DocView) уже дождался готовности воркера
// раньше, чем успешно отрендерил свой <Document> — этот компонент монтируется
// только следом. Вызов идемпотентен (см. pdfWorkerSetup.js), достаточно на
// всякий случай подтолкнуть загрузку, если модуль вдруг используется отдельно.
ensurePdfWorker();

// Единый слот миниатюры — тот же размер, что был раньше (64×90, пропорция
// A4). Страница вписывается В ЭТОТ слот по своему РЕАЛЬНОМУ соотношению
// сторон (см. pdfFit.js): иначе чек или другой нестандартный формат давал
// миниатюру в разы выше соседних — полоса миниатюр разъезжалась.
const THUMB_BOX = { width: 64, height: 90 };

// Общая панель миниатюр для PdfView и DocView.
// Рендерит только страницы из `pages` (учёт first/last/custom),
// переиспользуя уже загруженный blob URL через тот же `file`.
function PreviewThumbnails({ file, pdfProxy, pages, currentPage, onSelect, printColor }) {
    const [aspects, setAspects] = useState({}); // pageNumber -> width/height

    useEffect(() => {
        if (!pdfProxy || !pages || pages.length === 0) return undefined;
        let cancelled = false;
        Promise.all(
            pages.map((p) =>
                pdfProxy
                    .getPage(p)
                    .then((page) => {
                        const viewport = page.getViewport({ scale: 1 });
                        return [p, viewport.width / viewport.height];
                    })
                    .catch(() => [p, FALLBACK_ASPECT])
            )
        ).then((entries) => {
            if (!cancelled) setAspects(Object.fromEntries(entries));
        });
        return () => {
            cancelled = true;
        };
    }, [pdfProxy, pages]);

    if (!file || !pages || pages.length === 0) return null;

    const grayscale = printColor === "black_white" ? "grayscale" : "";

    return (
        <div className="w-full flex flex-col gap-[0.8vh]">
            <Document file={file} loading={null} error={null}>
                {/* Внешний блок — реальная граница скролла (w-full, не
                    схлопывается), внутренний — сама дорожка, её ширина по
                    контенту (w-max) + mx-auto. Когда миниатюр мало и они
                    помещаются — дорожка уже контейнера и центрируется;
                    когда не помещаются — просто шире контейнера и скроллится,
                    justify-center здесь обрезал бы первую миниатюру. */}
                <div className="w-full overflow-x-auto py-[0.3vh] px-[0.2vh]">
                    <div className="flex flex-row gap-[0.8vh] mx-auto w-max">
                        {pages.map((page) => {
                            const isActive = page === currentPage;
                            const aspect = aspects[page] || FALLBACK_ASPECT;
                            const thumbWidth =
                                fitWidth(aspect, THUMB_BOX.width, THUMB_BOX.height) || THUMB_BOX.width;
                            return (
                                <motion.button data-analytics="previewthumbnails.onSelect.1"
                                    key={page}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => onSelect(page)}
                                    title={`${page}`}
                                    style={{ width: THUMB_BOX.width, height: THUMB_BOX.height }}
                                    className={`relative flex-none flex items-center justify-center rounded-[1vh] overflow-hidden bg-white border-[0.25vh] transition-colors ${
                                        isActive
                                            ? "border-[#246CE2] shadow-[0_0_0_0.3vh_#E3ECFB]"
                                            : "border-[#E1E4EB]"
                                    }`}
                                >
                                    <div className={grayscale}>
                                        <Page
                                            pageNumber={page}
                                            width={thumbWidth}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                            loading={
                                                <div className="w-16 h-[90px] bg-[#F1F3F7] animate-pulse" />
                                            }
                                        />
                                    </div>
                                    <span className="absolute left-[0.4vh] bottom-[0.4vh] bg-[rgba(18,22,31,0.72)] text-white text-[1vh] font-extrabold rounded-[0.6vh] px-[0.6vh]">
                                        {page}
                                    </span>
                                </motion.button>
                            );
                        })}
                    </div>
                </div>
            </Document>
        </div>
    );
}

export default PreviewThumbnails;
